import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    FaAddressBook, FaArchive, FaBuilding, FaEnvelope, FaPen, FaPhoneAlt,
    FaPlus, FaRegStar, FaSearch, FaStar, FaUsers, FaEyeSlash, FaSlidersH,
} from "react-icons/fa";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { EmptyState, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { ContactPreview } from "./ContactPreview";
import { onMatchesSearch } from "../../../../_UI/SearchFilter";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";

const firstPrimary = (rows = [], key) => {
    const list = Array.isArray(rows) ? rows : [];
    return list.find((row) => row?.isPrimary)?.[key] || list[0]?.[key] || "";
};

const initials = (name = "") => String(name || "C").trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]).join("").toUpperCase() || "C";

const SEARCH_SKIP_KEYS = new Set(["photoDataUrl"]);

const flattenSearchValues = (value, key = "") => {
    if (SEARCH_SKIP_KEYS.has(key) || value === null || value === undefined) return [];
    if (["string", "number", "boolean"].includes(typeof value)) return [String(value)];
    if (Array.isArray(value)) return value.flatMap((item) => flattenSearchValues(item));
    if (typeof value === "object") return Object.entries(value).flatMap(([childKey, childValue]) => flattenSearchValues(childValue, childKey));
    return [];
};

const matches = (contact, query) => onMatchesSearch(query, flattenSearchValues(contact));

const CONTACT_TYPE_OPTIONS = [
    { value: "ALL", label: "Todos" },
    { value: "PERSONAL", label: "Personal" }, { value: "WORK", label: "Trabajo / compañero" },
    { value: "FAMILY", label: "Familia" }, { value: "FRIEND", label: "Amigo / conocido" },
    { value: "CLIENT", label: "Cliente" }, { value: "LEAD", label: "Prospecto" },
    { value: "SUPPLIER", label: "Proveedor" }, { value: "PARTNER", label: "Socio / partner" },
    { value: "PROFESSIONAL", label: "Profesional" }, { value: "HEALTH", label: "Salud / médico" },
    { value: "EDUCATION", label: "Educación / docente" }, { value: "SERVICE", label: "Servicio / comercio" },
    { value: "GOVERNMENT", label: "Organismo / trámite" }, { value: "EMERGENCY", label: "Emergencia" },
    { value: "OTHER", label: "Otro" },
];

export const ContactGroupWorkspaceModal = ({
    isOpen,
    scope = "ALL",
    group = null,
    groups = [],
    contacts = [],
    initialSearch = "",
    onClose,
    onNewContact,
    onEditContact,
    onEditGroup,
    onToggleFavorite,
    onArchiveContact,
    isLoading = false,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", type: "ALL", organization: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const advancedCount = [
        advanced.favorite !== "ALL", advanced.type !== "ALL", String(advanced.organization || "").trim(),
        advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact,
    ].filter(Boolean).length;

    const scopedContacts = useMemo(() => (Array.isArray(contacts) ? contacts : [])
        .filter((contact) => scope === "ALL"
            || (scope === "NONE" ? !contact.groupId : String(contact.groupId) === String(scope)))
        .sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite))
            || String(a.displayName || "").localeCompare(String(b.displayName || ""), "es", { sensitivity: "base" })),
    [contacts, scope]);

    const visible = useMemo(() => scopedContacts.filter((contact) => {
        if (!matches(contact, search)) return false;
        if (advanced.favorite !== "ALL" && (!!contact.isFavorite !== (advanced.favorite === "YES"))) return false;
        if (advanced.type !== "ALL" && String(contact.contactType || "") !== String(advanced.type)) return false;
        if (String(advanced.organization || "").trim() && !onMatchesSearch(advanced.organization, [contact.organization, contact.jobTitle, contact.department])) return false;
        const dateValue = advanced.dateField === "CREATED" ? contact.createdAt : contact.updatedAt;
        return onDateMatches(dateValue, advanced.dateRange, advanced.dateExact);
    }), [scopedContacts, search, advanced]);
    const selected = visible.find((contact) => String(contact.id) === String(selectedId)) || null;
    const selectedGroup = selected?.groupId
        ? groups.find((item) => String(item.id) === String(selected.groupId)) || null
        : null;

    const title = scope === "ALL" ? "Todos los contactos" : scope === "NONE" ? "Sin grupo" : group?.name || "Grupo de contactos";
    const description = scope === "ALL" || scope === "NONE"
        ? null
        : (group?.description || null);

    useEffect(() => {
        if (!isOpen) return;
        setSearch(String(initialSearch || ""));
        setAdvancedOpen(searchParams.get("contactAdvanced") === "1");
        // No autoseleccionar: restaurar sólo lo que venga explícitamente en la URL.
        setSelectedId(searchParams.get("contact") || null);
    }, [isOpen, scope, initialSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOpen || isLoading) return;
        const routed = searchParams.get("contact");
        if (!routed) return;
        if (scopedContacts.some((contact) => String(contact.id) === String(routed))) {
            setSelectedId(String(routed));
        }
        // No borres el id de la URL mientras hidratan los datos.
        // Si la API tarda un render, el registro sigue siendo recuperable al recargar.
    }, [isOpen, isLoading, searchParams, scopedContacts, setSearchParams]);

    const updateRouteState = (patch = {}) => {
        const next = new URLSearchParams(window.location.search);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") next.delete(key);
            else next.set(key, String(value));
        });
        setSearchParams(next, { replace: true });
    };

    const selectContact = (id) => {
        setSelectedId(id);
        updateRouteState({ contact: id, preview: null });
    };

    const hidePreview = () => {
        setSelectedId(null);
        updateRouteState({ contact: null, preview: null });
    };
    const toggleAdvanced = () => {
        const next = !advancedOpen;
        setAdvancedOpen(next);
        updateRouteState({ contactAdvanced: next ? "1" : null });
    };

    return <AppModal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        className="LStudio__contacts_group_workspace_modal"
        title={title}
        description={description}
    >
        <div className="LStudio__contacts_group_workspace">
            <aside className="LStudio__contacts_group_workspace_sidebar">
                <div className="LStudio__contacts_group_workspace_actions">
                    <AppButton size="sm" icon={<FaPlus />} onClick={() => onNewContact?.(scope === "ALL" || scope === "NONE" ? null : group)}>Nuevo contacto</AppButton>
                    {group && <AppButton size="sm" variant="outline" tone="neutral" icon={<FaPen />} onClick={() => onEditGroup?.(group)}>Editar grupo</AppButton>}
                </div>

                <div className="LStudio__contacts_group_workspace_filterbar">
                    <label className="LStudio__contacts_group_workspace_search">
                        <FaSearch />
                        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar dentro del grupo..." />
                    </label>
                    <button type="button" className="LStudio__contacts_group_workspace_filter_toggle" data-open={advancedOpen ? "true" : "false"}
                        onClick={toggleAdvanced} title="Filtros avanzados" aria-label="Filtros avanzados"><FaSlidersH /></button>
                </div>
                <AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={() => setAdvanced({ favorite: "ALL", type: "ALL", organization: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null })}
                    className="LStudio__contacts_group_workspace_advanced">
                    <FieldSelect id="contact_ws_favorite" label="Destacado" value={advanced.favorite} onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))}
                        options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                    <FieldSelect id="contact_ws_type" label="Tipo" value={advanced.type} onChange={(event) => setAdvanced((current) => ({ ...current, type: event.target.value }))}
                        options={CONTACT_TYPE_OPTIONS} />
                    <FieldInput id="contact_ws_org" label="Organización / cargo" value={advanced.organization} onChange={(event) => setAdvanced((current) => ({ ...current, organization: event.target.value }))} />
                    <FieldSelect id="contact_ws_date" label="Fecha" value={advanced.dateField} onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))}
                        options={[{ value: "UPDATED", label: "Actualización" }, { value: "CREATED", label: "Creación" }]} />
                    <FieldDate id="contact_ws_from" label="Desde" value={advanced.dateRange?.[0] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                    <FieldDate id="contact_ws_to" label="Hasta" value={advanced.dateRange?.[1] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                    <FieldDate id="contact_ws_exact" label="Fecha exacta" value={advanced.dateExact} onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                </AdvancedFilterPanel>

                <div className="LStudio__contacts_group_workspace_count">
                    {visible.length} {visible.length === 1 ? "contacto" : "contactos"}
                </div>

                <div className="LStudio__contacts_group_workspace_list">
                    {visible.map((contact) => {
                        const phone = firstPrimary(contact.phones, "number");
                        const email = firstPrimary(contact.emails, "email");
                        return <button key={contact.id} type="button"
                            data-active={String(contact.id) === String(selectedId)}
                            onClick={() => selectContact(contact.id)}>
                            <span className="LStudio__contacts_group_workspace_avatar">
                                {(contact.photoDataUrl || contact.photoUrl)
                                    ? <img src={contact.photoDataUrl || contact.photoUrl} alt="" />
                                    : initials(contact.displayName)}
                            </span>
                            <span className="LStudio__contacts_group_workspace_item_body">
                                <span className="LStudio__contacts_group_workspace_item_title">
                                    <strong>{contact.displayName || "Sin nombre"}</strong>
                                    {contact.isFavorite && <FaStar title="Destacado" />}
                                </span>
                                <small>{contact.contactType || "Contacto"}{contact.organization ? ` · ${contact.organization}` : ""}</small>
                                <span className="LStudio__contacts_group_workspace_item_quick">
                                    {phone && <span><FaPhoneAlt /> {phone}</span>}
                                    {!phone && email && <span><FaEnvelope /> {email}</span>}
                                    {!phone && !email && contact.organization && <span><FaBuilding /> {contact.organization}</span>}
                                </span>
                            </span>
                        </button>;
                    })}
                    {!visible.length && <div className="LStudio__contacts_group_workspace_empty_list">
                        {scopedContacts.length ? "No hay contactos que coincidan con la búsqueda." : "Este grupo todavía no tiene contactos."}
                    </div>}
                </div>
            </aside>

            <main className="LStudio__contacts_group_workspace_preview">
                {selected ? <>
                    <div className="LStudio__contacts_group_workspace_preview_actions">
                        <div className="LStudio__contacts_group_workspace_preview_title">
                            <strong>{selected.displayName || "Contacto"}</strong>
                            <div>
                                {selectedGroup && <Badge tone="neutral" size="sm">{selectedGroup.name}</Badge>}
                                {!selectedGroup && <Badge tone="neutral" size="sm">Sin grupo</Badge>}
                            </div>
                        </div>
                        <div className="LStudio__contacts_group_workspace_preview_buttons">
                            <AppButton size="sm" variant="ghost" tone="neutral"
                                icon={selected.isFavorite ? <FaStar /> : <FaRegStar />}
                                onClick={() => onToggleFavorite?.(selected)}>
                                {selected.isFavorite ? "Quitar destacado" : "Destacar"}
                            </AppButton>
                            <AppButton size="sm" variant="outline" tone="neutral" icon={<FaPen />} onClick={() => onEditContact?.(selected)}>Editar</AppButton>
                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaEyeSlash />} onClick={hidePreview}>Ocultar</AppButton>
                            <AppButton size="sm" variant="outline" tone="danger" icon={<FaArchive />} onClick={() => onArchiveContact?.(selected)}>Archivar</AppButton>
                        </div>
                    </div>
                    <div className="LStudio__contacts_group_workspace_preview_scroll">
                        <ContactPreview contact={selected} group={selectedGroup} />
                    </div>
                </> : <EmptyState
                    icon={<FaAddressBook />}
                    title={scopedContacts.length ? "Elegí un contacto" : "Este grupo está vacío"}
                    description={scopedContacts.length ? "Seleccioná un contacto de la izquierda para ver la ficha completa acá." : "Creá el primer contacto y va a quedar organizado dentro de este grupo."}
                    action={!scopedContacts.length ? <AppButton icon={<FaPlus />} onClick={() => onNewContact?.(scope === "ALL" || scope === "NONE" ? null : group)}>Nuevo contacto</AppButton> : null}
                />}
            </main>
        </div>
    </AppModal>;
};
